import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import { computed, ref } from 'vue';
import api from '@/app/api/axios.js';
import Bugsnag from '@bugsnag/js';

export const useAuthStore = defineStore('auth', () => {
    const accessToken = useLocalStorage('accessToken', null);
    const refreshToken = useLocalStorage('refreshToken', null);
    const accessTokenExpiry = useLocalStorage('accessTokenExpiry', 0);

    const user = ref(null);

    const profilePicture = ref('');
    const isProfilePictureLoading = ref(false);

    const isAuthenticated = computed(() => !!accessToken.value && !!user.value);
    const isVerified = computed(() => !!user.value?.attributes?.email_verified);
    const hasPendingChange = computed(() => !!user.value?.attributes?.email_change_pending);
    const has2FA = computed(() => !!user.value?.attributes?.two_factor_enabled);

    const language = computed(() => user.value?.attributes?.language);
    const firstName = computed(() => user.value?.attributes?.first_name);
    const fullName = computed(() => {
        if (!user.value?.attributes) {
            return '';
        }

        return `${firstName.value} ${user.value.attributes.last_name}`.trim();
    });
    const initials = computed(() => user.value?.attributes?.initials ?? '');

    /**
     * Revokes the current profile picture object URL to prevent memory leaks
     */
    const revokeProfilePictureUrl = () => {
        if (profilePicture.value && profilePicture.value.startsWith('blob:')) {
            const urlCreator = window.URL || window.webkitURL;
            urlCreator.revokeObjectURL(profilePicture.value);
        }
    };

    /**
     * Sets a new profile picture URL, revoking the old one first
     * @param {string} url - The new profile picture URL (object URL or regular URL)
     */
    const setProfilePictureUrl = (url) => {
        revokeProfilePictureUrl();
        profilePicture.value = url;
    };

    /**
     * Fetches the profile picture from the API and creates an object URL
     */
    const getProfilePicture = async () => {
        if (!user.value) {
            return;
        }

        isProfilePictureLoading.value = true;

        try {
            const response = await api.get(`/general/users/${user.value.id}/profile-picture`, {
                responseType: 'blob',
            });

            const urlCreator = window.URL || window.webkitURL;
            const newUrl = urlCreator.createObjectURL(response.data);
            setProfilePictureUrl(newUrl);
        } catch {
            // non-critical — user continues with default avatar
        } finally {
            isProfilePictureLoading.value = false;
        }
    };

    /**
     * Sets the user data in the store
     * @param newUser
     */
    const setUser = (newUser) => {
        user.value = newUser;
    };

    /**
     * Resends the email verification to the user
     * @returns {Promise<axios.AxiosResponse<any>>}
     */
    const resendVerifyEmail = () => {
        return api.get('/general/verifications/email');
    };

    /**
     * Hydrates the user store by fetching the user profile from the API
     * @returns {Promise<void>}
     */
    const hydrate = async () => {
        const { data } = await api.get('/general/profile');

        user.value = data.data;

        void getProfilePicture();

        // Set Bugsnag user for error tracking
        if (user.value?.id && user.value?.attributes?.email) {
            Bugsnag.setUser(user.value.id, user.value.attributes.email);
        }

        Bugsnag.leaveBreadcrumb('User hydrated', {
            type: 'state',
            metadata: {
                hasUser: !!user.value,
                isVerified: user.value?.attributes?.email_verified || false,
            },
        });
    };

    /**
     * Dehydrates the user store by clearing user data and profile picture
     * @returns {Promise<void>}
     */
    const dehydrate = async () => {
        // Clear Bugsnag user context
        Bugsnag.setUser(null);

        revokeProfilePictureUrl();
        user.value = null;
        profilePicture.value = '';
        isProfilePictureLoading.value = false;
    };

    return {
        accessToken,
        refreshToken,
        accessTokenExpiry,
        user,
        profilePicture,
        isProfilePictureLoading,
        isAuthenticated,
        isVerified,
        hasPendingChange,
        has2FA,
        language,
        firstName,
        fullName,
        initials,
        revokeProfilePictureUrl,
        setProfilePictureUrl,
        getProfilePicture,
        setUser,
        resendVerifyEmail,
        hydrate,
        dehydrate,
    };
});
